import { PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { appActions } from 'app/app.actions';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { SidekickLauncher } from './components/sidekickLauncher/sidekickLauncher';
import {
  SidekickWidget,
  type PendingPrompt,
} from './components/sidekickWidget/sidekickWidget';

export const Sidekick = ({
  className,
}: PropsWithClassName): JSX.Element | null => {
  const flags = useFeatureFlag(['mim-sidekick']);
  const dispatch = useAppDispatch();

  const organizationId = useAppSelector(
    (state) => state.settings.organization.organization.id,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<PendingPrompt | null>(
    null,
  );

  const isAskingRef = useRef(false);

  // Keeps the widget mounted after the first open; the chat session lives in it.
  const hasOpenedRef = useRef(false);
  if (isOpen) hasOpenedRef.current = true;

  const start = useCallback((text: string, newChat?: boolean) => {
    setPendingPrompt({
      id: uuid(),
      text,
      chatId: newChat ? uuid() : undefined,
    });
    setIsOpen(true);
  }, []);

  const minimize = useCallback(() => {
    setIsOpen(false);
    // A prompt that never made it out is not owed to a later reopen.
    setPendingPrompt(null);
  }, []);

  const markUnread = useCallback(() => setHasUnread(true), []);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe(
      'notifySidekick',
      ({ text, summary }) => {
        if (!hasOpenedRef.current) return;
        setPendingPrompt({ id: uuid(), text, pageEventSummary: summary });
      },
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe('toggleSidekick', ({ isOpen: next }) =>
      setIsOpen((prev) => next ?? !prev),
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (isOpen) setHasUnread(false);
  }, [isOpen]);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe('askSidekick', (request) => {
      // Nothing can come of asking while the Sidekick cannot be opened.
      if (!flags.mimSidekick) return;

      if ('prompt' in request) {
        start(request.prompt, request.newChat);
        return;
      }

      // A second modal of the same type never reaches the stack, and would
      // leave this promise unsettled.
      if (isAskingRef.current) return;
      isAskingRef.current = true;

      void (async () => {
        const result = await dispatch(
          appActions.showModal({
            modal: {
              type: 'app:sidekickAsk',
              arg: {
                title: request.title,
                label: request.label,
                placeholder: request.placeholder,
                choices: request.choices,
                defaultAnswer: request.defaultAnswer,
              },
            },
          }),
        );

        isAskingRef.current = false;

        if (result.isCancelled) return;
        start(request.createPrompt(result.answer));
      })();
    });
    return unsubscribe;
  }, [dispatch, flags.mimSidekick, start]);

  if (!flags.mimSidekick || !organizationId) return null;

  return (
    <>
      {!isOpen && (
        <SidekickLauncher
          hasUnread={hasUnread}
          autoFocus={hasOpenedRef.current}
          onClick={() => setIsOpen(true)}
        />
      )}
      {hasOpenedRef.current && (
        <SidekickWidget
          className={className ?? ''}
          isMinimized={!isOpen}
          pendingPrompt={pendingPrompt}
          onPromptSent={() => setPendingPrompt(null)}
          onMinimize={minimize}
          onUnread={markUnread}
        />
      )}
    </>
  );
};
